import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "config/axiosConfig";
import CollectionCardRefactored, {
  type CollectionEntity,
  type CollectionGraphSnapshotData,
} from "components/Collections/CollectionCardRefactored";
import { collectionQueryKeys, useGraphSnapshotQuery } from "components/Collections/useCollections";
import { graphMessage, resolveGraphLocale } from "components/Graph/graphCopy";
import {
  applyPendingMediaOrder,
  syncPostCoauthors,
} from "components/Posts/display/postProfileHelpers";
import { usePostProfileController } from "components/Posts/display/usePostProfileController";
import {
  normalizeExistingCoauthors,
  usePostCoauthorState,
} from "components/Posts/display/usePostCoauthorState";
import {
  mapCollectionItemsToLibraryDraftItems,
  type LibraryDraftItem,
} from "components/Posts/LibraryCollectionForm";
import PostCommentsTabs from "components/Posts/display/PostCommentsTabs";
import { collectionApi, mediaAssetsApi } from "lib/api";
import { getCollectionUiStrings, resolveCollectionLocale } from "i18n/collectionUi";
import LoadingSkeleton from "ui/LoadingSkeleton";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import { apiRoutes } from "util/apiRoutes";
import type { EntityCardRef } from "components/Entities/EntityCardBase";
import type { PrivacyLevel } from "util/userUtils";

function normalizeCollectionType(value: unknown): "standard" | "graph" | "library" {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "graph") {
    return "graph";
  }
  if (normalized === "library") {
    return "library";
  }
  return "standard";
}

function getLibraryDraftKey(item: LibraryDraftItem): string | null {
  if (item.kind === "media") {
    return item.entityId ? `media_asset:${item.entityId}` : null;
  }
  return `url_metadata:${item.entityId}`;
}

function normalizeCollectionPrivacy(value: unknown): PrivacyLevel {
  const normalized = String(value || "").trim().toLowerCase();
  switch (normalized) {
    case "follow":
      return "follow";
    case "members":
      return "members";
    case "teams":
      return "teams";
    case "besteas":
      return "besteas";
    case "me":
      return "me";
    case "admin":
      return "admin";
    case "anonymous":
      return "anonymous";
    case "public":
    default:
      return "public";
  }
}

const CollectionProfile: React.FC = () => {
  const ui = getCollectionUiStrings(resolveCollectionLocale());
  const graphLocale = resolveGraphLocale();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [referenceLink, setReferenceLink] = useState("");
  const [privacy, setPrivacy] = useState<PrivacyLevel>("public");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [libraryItems, setLibraryItems] = useState<LibraryDraftItem[]>([]);
  const [initialLibraryItems, setInitialLibraryItems] = useState<LibraryDraftItem[]>([]);
  const collectionCardRef = useRef<EntityCardRef>(null);

  const {
    entity: collection,
    setEntity: setCollection,
    error,
    isLoading,
    editMode,
    setEditMode,
    setSearchParams,
  } = usePostProfileController<CollectionEntity>({
    id,
    fallbackKind: "collection",
    getDocumentTitle: (entity) => `${ui.collectionKindLabel} - ${entity?.title || ""}`,
    notFoundError: "404: Failed to load collection profile.",
    genericError: "Failed to load collection profile.",
  });

  const collectionType = normalizeCollectionType(collection?.properties?.collection_type);
  const {
    newCoauthors,
    removedCoauthors,
    initializeCoauthors,
    handleCoauthorsChange,
    resetCoauthorChanges,
  } = usePostCoauthorState(privacy);

  const libraryItemsQuery = useQuery({
    queryKey:
      collection && collectionType === "library"
        ? collectionQueryKeys.collectionItems(Number(collection.id))
        : ["collections", "items", "skip"],
    queryFn: () => collectionApi.getItems(Number(collection?.id || 0)),
    enabled: Boolean(collection?.id) && collectionType === "library",
  });

  /**
   * For graph collections, fetch the full graph snapshot so the card can
   * render the GraphCanvas (or GraphCanvasSkeleton while loading) above the
   * media area. The shared hook centralizes caching and mapping so the same
   * snapshot can be used by CollectionCardRefactored in lists without
   * duplicate requests.
   */
  const graphSnapshotQuery = useGraphSnapshotQuery(
    collection && collectionType === "graph" ? Number(collection.id) : null
  );

  const graphSnapshotData: CollectionGraphSnapshotData | null =
    graphSnapshotQuery.data ?? null;

  useEffect(() => {
    if (!collection) {
      return;
    }
    setTitle(collection.title || "");
    setDescription(collection.body || "");
    setReferenceLink(collection.url || "");
    setPrivacy(normalizeCollectionPrivacy(collection.properties?.privacy));
    initializeCoauthors(normalizeExistingCoauthors(collection.properties?.coauthors));
  }, [collection, initializeCoauthors]);

  useEffect(() => {
    if (collectionType !== "library" || !libraryItemsQuery.data) {
      return;
    }
    if (editMode && libraryItems.length > 0) {
      return;
    }
    const mappedItems = mapCollectionItemsToLibraryDraftItems(libraryItemsQuery.data);
    setLibraryItems(mappedItems);
    setInitialLibraryItems(mappedItems);
  }, [collectionType, editMode, libraryItems.length, libraryItemsQuery.data]);

  const syncLibraryItems = useCallback(
    async (collectionId: number) => {
      const orderedItems = [...libraryItems].sort((left, right) => left.sortOrder - right.sortOrder);
      const nextKeys = new Set(
        orderedItems
          .map((item) => getLibraryDraftKey(item))
          .filter((value): value is string => typeof value === "string" && value.length > 0)
      );

      const removedItems = initialLibraryItems.filter((item) => {
        const key = getLibraryDraftKey(item);
        return key !== null && !nextKeys.has(key);
      });

      for (const removedItem of removedItems) {
        await collectionApi.removeItem(
          collectionId,
          removedItem.kind === "media" ? "media_asset" : "url_metadata",
          removedItem.kind === "media"
            ? Number(removedItem.entityId)
            : removedItem.entityId
        );
      }

      const uploadItems = orderedItems.filter(
        (
          item
        ): item is LibraryDraftItem & {
          kind: "media";
          source: "upload";
          file: File;
        } =>
          item.kind === "media" && item.source === "upload" && item.file instanceof File
      );
      const uploadedAssets = await mediaAssetsApi.uploadStandalone(
        uploadItems.map((item) => ({
          file: item.file,
          order: item.sortOrder,
        })),
        {
          audience: privacy,
          properties: {
            collection_id: collectionId,
            collection_type: "library",
          },
        }
      );

      let uploadIndex = 0;
      for (const item of orderedItems) {
        if (item.kind === "media") {
          const mediaAssetId =
            item.source === "existing" ? item.entityId : uploadedAssets[uploadIndex++]?.id;
          if (!mediaAssetId) {
            throw new Error("LIBRARY_MEDIA_ATTACH_FAILED");
          }
          await collectionApi.addItem(collectionId, {
            entityId: mediaAssetId,
            entityType: "media_asset",
            properties: { sort_order: item.sortOrder },
          });
          continue;
        }

        await collectionApi.addItem(collectionId, {
          entityId: item.entityId,
          entityType: "url_metadata",
          properties: { sort_order: item.sortOrder },
        });
      }

      await queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.collectionItems(collectionId),
      });
      const refreshedItems = await collectionApi.getItems(collectionId);
      const mappedItems = mapCollectionItemsToLibraryDraftItems(refreshedItems);
      setLibraryItems(mappedItems);
      setInitialLibraryItems(mappedItems);
    },
    [initialLibraryItems, libraryItems, privacy, queryClient]
  );

  const handleSave = async () => {
    if (!collection || isSubmitting) {
      return;
    }

    if (referenceLink.trim().length > 0) {
      try {
        const parsedUrl = new URL(referenceLink.trim());
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          toast.error(ui.invalidHttpsUrl);
          return;
        }
      } catch {
        toast.error(ui.invalidHttpsUrl);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const mediaOrderSuccess = await applyPendingMediaOrder(collectionCardRef);
      if (!mediaOrderSuccess) {
        return;
      }

      await api.patch(apiRoutes.posts.byId(id || ""), {
        title,
        body: description,
        url: collectionType === "library" ? null : referenceLink.trim() || null,
        privacy,
        properties: {
          collection_type: collectionType,
        },
      });

      await syncPostCoauthors("collection", collection.id, newCoauthors, removedCoauthors, privacy);

      if (collectionType === "library") {
        await syncLibraryItems(Number(collection.id));
      }

      setCollection((previous) =>
        previous
          ? {
              ...previous,
              title,
              body: description,
              url: collectionType === "library" ? null : referenceLink.trim() || null,
              properties: {
                ...previous.properties,
                privacy,
                collection_type: collectionType,
              },
            }
          : null
      );
      setEditMode(false);
      resetCoauthorChanges();
      setSearchParams({});
      toast.success(ui.updateSuccess);
    } catch {
      toast.error(ui.updateError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (collection) {
      setTitle(collection.title || "");
      setDescription(collection.body || "");
      setReferenceLink(collection.url || "");
      setPrivacy(normalizeCollectionPrivacy(collection.properties?.privacy));
      initializeCoauthors(normalizeExistingCoauthors(collection.properties?.coauthors));
    }
    if (collectionType === "library" && libraryItemsQuery.data) {
      const mappedItems = mapCollectionItemsToLibraryDraftItems(libraryItemsQuery.data);
      setLibraryItems(mappedItems);
      setInitialLibraryItems(mappedItems);
    }
    setEditMode(false);
    resetCoauthorChanges();
    setSearchParams({});
  };

  if (error) {
    return error.match(/404/) ? (
      <NotFoundPage
        displayStyle="page"
        errorMessage={ui.collectionNotFoundTitle}
        errorSubtext={ui.collectionNotFoundSubtext}
        buttonText={ui.goHome}
        buttonHref="/"
      />
    ) : (
      <ServerErrorPage />
    );
  }

  if (isLoading || !collection) {
    return (
      <LoadingSkeleton 
        count={1} 
        variant={collectionType === "graph" ? "graph" : "entity-card"} 
        margin="mt-4" 
        width="max-w-5xl" 
        ariaLabel={graphMessage(graphLocale, "loadingGraph")}
      />
    );
  }

  const editorButtons = editMode ? (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        {ui.cancel}
      </button>
      <button
        type="button"
        onClick={() => void handleSave()}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting}
      >
        {ui.save}
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 relative">
      <CollectionCardRefactored
        ref={collectionCardRef}
        collection={{
          ...collection,
          title,
          body: description,
          url: collectionType === "library" ? null : referenceLink,
          properties: {
            ...collection.properties,
            privacy,
            collection_type: collectionType,
          },
        }}
        referenceLink={referenceLink}
        onReferenceLinkChange={setReferenceLink}
        libraryItems={libraryItems}
        onLibraryItemsChange={setLibraryItems}
        graphSnapshot={graphSnapshotData}
        isEntityProfilePage={true}
        truncateDescription={true}
        editMode={editMode}
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        onPrivacyChange={(value) => setPrivacy(normalizeCollectionPrivacy(value))}
        onCoauthorsChange={handleCoauthorsChange}
      >
        {editorButtons}
      </CollectionCardRefactored>
      <PostCommentsTabs
        kind="collection"
        entityId={Number(collection.id)}
        entityTitle={collection.title}
      />
    </div>
  );
};

export default CollectionProfile;
